import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  FileText,
  CheckSquare,
  Square,
  Loader2,
  AlertCircle,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';
import toast from 'react-hot-toast';

// PDF-lib will be used for page extraction
// pdfjs-dist will be used for rendering thumbnails
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from '../utils/xlsxCompat';

// Set worker path for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface ClientPDFPageSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  file: File;
  onPagesExtracted: (extractedFile: File, selectedPages: number[], originalPageCount: number) => void;
  onSkip: () => void;
}

interface PageThumbnail {
  pageNumber: number;
  dataUrl: string;
  width: number;
  height: number;
}

const ClientPDFPageSelector: React.FC<ClientPDFPageSelectorProps> = ({
  isOpen,
  onClose,
  file,
  onPagesExtracted,
  onSkip
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [thumbnails, setThumbnails] = useState<PageThumbnail[]>([]);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [rangeInput, setRangeInput] = useState('');
  const [previewPage, setPreviewPage] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0.6);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<any>(null);

  const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  const isImage = file.type.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp)$/i.test(file.name);
  const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                  file.type === 'application/vnd.ms-excel' ||
                  /\.(xlsx|xls)$/i.test(file.name);

  useEffect(() => {
    if (isOpen && file) {
      if (isPDF) {
        loadPDF();
      } else if (isImage) {
        loadImage();
      } else if (isExcel) {
        loadExcel();
      } else {
        loadOtherFile();
      }
    }
  }, [isOpen, file]);

  // Load Excel file and create sheet previews
  const loadExcel = async () => {
    setLoading(true);
    setError(null);
    setThumbnails([]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = await XLSX.readAsync(arrayBuffer);
      const sheetNames = workbook.sheets.map(s => s.name);

      if (sheetNames.length === 0) {
        throw new Error('No sheets found in Excel file');
      }

      const sheetThumbnails: PageThumbnail[] = [];

      for (let i = 0; i < workbook.sheets.length; i++) {
        const sheetName = sheetNames[i];
        const worksheet = workbook.sheets[i];

        // Convert sheet to array of arrays
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        // Create canvas preview for this sheet
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (context) {
          canvas.width = 600;
          canvas.height = 800;

          // White background
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);

          // Sheet name header
          context.fillStyle = '#1e40af';
          context.fillRect(0, 0, canvas.width, 40);
          context.fillStyle = '#ffffff';
          context.font = 'bold 14px sans-serif';
          context.textAlign = 'left';
          context.fillText(`Sheet ${i + 1}: ${sheetName}`, 10, 26);

          // Table styling
          context.fillStyle = '#111827';
          context.font = '11px monospace';
          context.textAlign = 'left';

          const startY = 55;
          const rowHeight = 18;
          const colWidth = 100;
          const maxRows = Math.min(data.length, 40);
          const maxCols = Math.min(5, Math.max(...data.map(row => row?.length || 0)));

          // Draw grid and data
          for (let row = 0; row < maxRows; row++) {
            const rowData = data[row] || [];
            const y = startY + (row * rowHeight);

            // Alternate row background
            if (row % 2 === 0) {
              context.fillStyle = '#f9fafb';
              context.fillRect(0, y - 12, canvas.width, rowHeight);
            }

            // Draw cells
            context.fillStyle = row === 0 ? '#1f2937' : '#374151';
            context.font = row === 0 ? 'bold 11px monospace' : '11px monospace';

            for (let col = 0; col < maxCols; col++) {
              const cellValue = rowData[col]?.toString() || '';
              const truncated = cellValue.length > 12 ? cellValue.substring(0, 10) + '..' : cellValue;
              const x = 10 + (col * colWidth);
              context.fillText(truncated, x, y);
            }
          }

          // Row count info
          if (data.length > maxRows) {
            context.fillStyle = '#6b7280';
            context.font = 'italic 10px sans-serif';
            context.fillText(`... and ${data.length - maxRows} more rows`, 10, startY + (maxRows * rowHeight) + 10);
          }

          // Border
          context.strokeStyle = '#e5e7eb';
          context.lineWidth = 2;
          context.strokeRect(0, 0, canvas.width, canvas.height);

          const dataUrl = canvas.toDataURL('image/png');
          sheetThumbnails.push({
            pageNumber: i + 1,
            dataUrl,
            width: canvas.width,
            height: canvas.height
          });
        }
      }

      setTotalPages(sheetNames.length);
      setSelectedPages(new Set(sheetNames.map((_, i) => i + 1)));
      setThumbnails(sheetThumbnails);

    } catch (err: any) {
      console.error('Error loading Excel:', err);
      setError(err.message || 'Failed to load Excel file');
    } finally {
      setLoading(false);
    }
  };

  const loadImage = async () => {
    setLoading(true);
    setError(null);
    setThumbnails([]);

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setTotalPages(1);
      setSelectedPages(new Set([1]));
      setThumbnails([{
        pageNumber: 1,
        dataUrl,
        width: 150,
        height: 200
      }]);

    } catch (err: any) {
      console.error('Error loading image:', err);
      setError(err.message || 'Failed to load image');
    } finally {
      setLoading(false);
    }
  };

  const loadOtherFile = async () => {
    setLoading(true);
    setError(null);
    setThumbnails([]);

    try {
      // Check if it's a text-based file we can preview
      const isTextFile = file.type === 'text/plain' ||
                         file.type === 'text/csv' ||
                         file.name.toLowerCase().endsWith('.csv') ||
                         file.name.toLowerCase().endsWith('.txt');

      if (isTextFile) {
        // Read text content and create paginated previews
        const text = await file.text();
        const allLines = text.split('\n');
        const linesPerPage = 45;
        const totalPageCount = Math.ceil(allLines.length / linesPerPage);

        const pageThumbnails: PageThumbnail[] = [];

        for (let pageNum = 0; pageNum < totalPageCount; pageNum++) {
          const startLine = pageNum * linesPerPage;
          const pageLines = allLines.slice(startLine, startLine + linesPerPage);

          // Create a canvas to render text preview
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (context) {
            canvas.width = 600;
            canvas.height = 800;

            // White background
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);

            // Page header
            context.fillStyle = '#1e40af';
            context.fillRect(0, 0, canvas.width, 35);
            context.fillStyle = '#ffffff';
            context.font = 'bold 12px sans-serif';
            context.textAlign = 'left';
            context.fillText(`Page ${pageNum + 1} of ${totalPageCount} - Lines ${startLine + 1}-${Math.min(startLine + linesPerPage, allLines.length)}`, 10, 23);

            // Text styling
            context.fillStyle = '#333333';
            context.font = '11px monospace';
            context.textAlign = 'left';

            // Draw text lines
            const lineHeight = 16;
            const padding = 10;
            const startY = 50;
            pageLines.forEach((line, index) => {
              const truncatedLine = line.length > 75 ? line.substring(0, 72) + '...' : line;
              context.fillText(truncatedLine, padding, startY + (index * lineHeight));
            });

            // Add border
            context.strokeStyle = '#e5e7eb';
            context.lineWidth = 2;
            context.strokeRect(0, 0, canvas.width, canvas.height);

            const dataUrl = canvas.toDataURL('image/png');

            pageThumbnails.push({
              pageNumber: pageNum + 1,
              dataUrl,
              width: canvas.width,
              height: canvas.height
            });
          }
        }

        setTotalPages(totalPageCount);
        setSelectedPages(new Set(pageThumbnails.map(t => t.pageNumber)));
        setThumbnails(pageThumbnails);

        if (pageThumbnails.length === 0) {
          throw new Error('Could not create canvas context');
        }
      } else {
        // For Excel, Word, etc. - create info card preview
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (context) {
          canvas.width = 400;
          canvas.height = 500;

          // White background
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);

          // File icon area
          context.fillStyle = '#f3f4f6';
          context.fillRect(125, 100, 150, 150);

          // File icon
          context.fillStyle = '#6b7280';
          context.font = '60px sans-serif';
          context.textAlign = 'center';
          context.fillText('📄', 200, 195);

          // File name
          context.fillStyle = '#111827';
          context.font = 'bold 16px sans-serif';
          context.textAlign = 'center';
          const displayName = file.name.length > 30 ? file.name.substring(0, 27) + '...' : file.name;
          context.fillText(displayName, 200, 300);

          // File type
          context.fillStyle = '#6b7280';
          context.font = '14px sans-serif';
          const fileExt = file.name.split('.').pop()?.toUpperCase() || 'FILE';
          context.fillText(`${fileExt} File`, 200, 330);

          // File size
          const sizeKB = (file.size / 1024).toFixed(1);
          const sizeDisplay = file.size > 1024 * 1024
            ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
            : `${sizeKB} KB`;
          context.fillText(sizeDisplay, 200, 360);

          // Border
          context.strokeStyle = '#e5e7eb';
          context.lineWidth = 2;
          context.strokeRect(0, 0, canvas.width, canvas.height);

          const dataUrl = canvas.toDataURL('image/png');

          setTotalPages(1);
          setSelectedPages(new Set([1]));
          setThumbnails([{
            pageNumber: 1,
            dataUrl,
            width: canvas.width,
            height: canvas.height
          }]);
        } else {
          throw new Error('Could not create canvas context');
        }
      }

    } catch (err: any) {
      console.error('Error loading file:', err);
      setError(err.message || 'Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    setThumbnails([]);

    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // Create a copy for pdf-lib (pdfjs-dist detaches the original)
      const pdfLibCopy = arrayBuffer.slice(0);
      setPdfBytes(pdfLibCopy);

      // Load PDF with pdf.js for rendering (uses original buffer)
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      pdfDocRef.current = pdf; // Store reference for high-res preview
      const numPages = pdf.numPages;
      setTotalPages(numPages);

      // Select all pages by default
      const allPages = new Set(Array.from({ length: numPages }, (_, i) => i + 1));
      setSelectedPages(allPages);

      // Generate thumbnails for each page
      const generatedThumbnails: PageThumbnail[] = [];

      for (let i = 1; i <= numPages; i++) {
        try {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.3 }); // Small scale for thumbnails

          // Create canvas for rendering
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (!context) {
            throw new Error('Could not get canvas context');
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          // Render page to canvas
          await page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas
          } as any).promise;

          // Convert to data URL
          const dataUrl = canvas.toDataURL('image/png');

          generatedThumbnails.push({
            pageNumber: i,
            dataUrl,
            width: viewport.width,
            height: viewport.height
          });
        } catch (pageError) {
          console.error(`Error rendering page ${i}:`, pageError);
          // Add placeholder for failed pages
          generatedThumbnails.push({
            pageNumber: i,
            dataUrl: '',
            width: 150,
            height: 200
          });
        }
      }

      setThumbnails(generatedThumbnails);

    } catch (err: any) {
      console.error('Error loading PDF:', err);
      setError(err.message || 'Failed to load PDF');
    } finally {
      setLoading(false);
    }
  };

  // Load high-resolution preview when previewPage changes
  useEffect(() => {
    if (previewPage === null) {
      setPreviewImage(null);
      setZoomLevel(0.6); // Reset zoom when closing
      return;
    }
    setZoomLevel(0.6); // Reset zoom when changing page

    const loadHighResPreview = async () => {
      setPreviewLoading(true);

      try {
        // For images, use the original dataUrl (already high-res)
        if (isImage && thumbnails[previewPage - 1]?.dataUrl) {
          setPreviewImage(thumbnails[previewPage - 1].dataUrl);
          setPreviewLoading(false);
          return;
        }

        // For Excel files, render high-res sheet preview
        if (isExcel) {
          const arrayBuffer = await file.arrayBuffer();
          const workbook = await XLSX.readAsync(arrayBuffer);
          const sheetIndex = previewPage - 1;
          const worksheet = workbook.sheets[sheetIndex];
          const sheetName = worksheet?.name ?? `Sheet ${previewPage}`;

          // Convert sheet to array of arrays
          const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

          // Create high-res canvas (2x resolution)
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (context) {
            canvas.width = 1200;
            canvas.height = 1600;

            // White background
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);

            // Sheet name header
            context.fillStyle = '#1e40af';
            context.fillRect(0, 0, canvas.width, 80);
            context.fillStyle = '#ffffff';
            context.font = 'bold 28px sans-serif';
            context.textAlign = 'left';
            context.fillText(`Sheet ${previewPage}: ${sheetName}`, 20, 52);

            // Table styling
            context.fillStyle = '#111827';
            context.font = '18px monospace';
            context.textAlign = 'left';

            const startY = 110;
            const rowHeight = 32;
            const colWidth = 180;
            const maxRows = Math.min(data.length, 45);
            const maxCols = Math.min(6, Math.max(...data.map(row => row?.length || 0)));

            // Draw grid and data
            for (let row = 0; row < maxRows; row++) {
              const rowData = data[row] || [];
              const y = startY + (row * rowHeight);

              // Alternate row background
              if (row % 2 === 0) {
                context.fillStyle = '#f9fafb';
                context.fillRect(0, y - 22, canvas.width, rowHeight);
              }

              // Draw cells
              context.fillStyle = row === 0 ? '#1f2937' : '#374151';
              context.font = row === 0 ? 'bold 18px monospace' : '18px monospace';

              for (let col = 0; col < maxCols; col++) {
                const cellValue = rowData[col]?.toString() || '';
                const truncated = cellValue.length > 18 ? cellValue.substring(0, 15) + '...' : cellValue;
                const x = 20 + (col * colWidth);
                context.fillText(truncated, x, y);
              }
            }

            // Row count info
            if (data.length > maxRows) {
              context.fillStyle = '#6b7280';
              context.font = 'italic 16px sans-serif';
              context.fillText(`... and ${data.length - maxRows} more rows`, 20, startY + (maxRows * rowHeight) + 20);
            }

            const highResDataUrl = canvas.toDataURL('image/png');
            setPreviewImage(highResDataUrl);
            setPreviewLoading(false);
            return;
          }
        }

        // For other non-PDF files (text), use the thumbnail
        if (!isPDF) {
          setPreviewImage(thumbnails[previewPage - 1]?.dataUrl || null);
          setPreviewLoading(false);
          return;
        }

        // For PDFs, render at high resolution
        if (!pdfDocRef.current) {
          setPreviewImage(thumbnails[previewPage - 1]?.dataUrl || null);
          setPreviewLoading(false);
          return;
        }

        const page = await pdfDocRef.current.getPage(previewPage);
        const viewport = page.getViewport({ scale: 3.0 }); // High resolution scale

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) {
          setPreviewImage(thumbnails[previewPage - 1]?.dataUrl || null);
          setPreviewLoading(false);
          return;
        }

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport: viewport,
          canvas: canvas
        } as any).promise;

        const highResDataUrl = canvas.toDataURL('image/png');
        setPreviewImage(highResDataUrl);

      } catch (err) {
        console.error('Error loading high-res preview:', err);
        // Fallback to thumbnail
        setPreviewImage(thumbnails[previewPage - 1]?.dataUrl || null);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadHighResPreview();
  }, [previewPage, thumbnails, isPDF, isImage, isExcel, file]);

  const togglePage = (pageNum: number) => {
    const newSelected = new Set(selectedPages);
    if (newSelected.has(pageNum)) {
      newSelected.delete(pageNum);
    } else {
      newSelected.add(pageNum);
    }
    setSelectedPages(newSelected);
  };

  const selectAll = () => {
    const allPages = new Set(Array.from({ length: totalPages }, (_, i) => i + 1));
    setSelectedPages(allPages);
  };

  const clearAll = () => {
    setSelectedPages(new Set());
  };

  const applyRange = () => {
    if (!rangeInput.trim()) return;

    const pages = parsePageRange(rangeInput, totalPages);
    setSelectedPages(new Set(pages));
    setRangeInput('');
  };

  const parsePageRange = (rangeString: string, maxPages: number): number[] => {
    const pages = new Set<number>();
    const parts = rangeString.split(',').map(s => s.trim());

    for (const part of parts) {
      if (part.includes('-')) {
        const [start, end] = part.split('-').map(s => parseInt(s.trim(), 10));
        if (!isNaN(start) && !isNaN(end)) {
          const validStart = Math.max(1, Math.min(start, maxPages));
          const validEnd = Math.max(1, Math.min(end, maxPages));
          for (let i = validStart; i <= validEnd; i++) {
            pages.add(i);
          }
        }
      } else {
        const pageNum = parseInt(part, 10);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= maxPages) {
          pages.add(pageNum);
        }
      }
    }

    return Array.from(pages).sort((a, b) => a - b);
  };

  const formatPageRangeForFilename = (pages: number[], total: number): string => {
    if (pages.length === total) {
      return 'all';
    }
    if (pages.length === 1) {
      return `p${pages[0]}`;
    }
    const ranges: string[] = [];
    let start = pages[0];
    let end = pages[0];
    for (let i = 1; i <= pages.length; i++) {
      if (i < pages.length && pages[i] === end + 1) {
        end = pages[i];
      } else {
        ranges.push(start === end ? String(start) : `${start}-${end}`);
        if (i < pages.length) {
          start = pages[i];
          end = pages[i];
        }
      }
    }
    let result = ranges.join('_');
    if (result.length > 30) {
      result = `${pages.length}pages`;
    }
    return result;
  };

  const handleExtractPages = async () => {
    if (selectedPages.size === 0) {
      toast.error('Please select at least one page');
      return;
    }

    setProcessing(true);

    try {
      const pagesArray = Array.from(selectedPages).sort((a, b) => a - b);

      if (isExcel) {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = await XLSX.readAsync(arrayBuffer);

        const newWorkbook = XLSX.utils.book_new();

        for (const pageNum of pagesArray) {
          const sheetIndex = pageNum - 1;
          const worksheet = workbook.sheets[sheetIndex];
          const sheetName = worksheet?.name ?? `Sheet ${pageNum}`;
          XLSX.utils.book_append_sheet(newWorkbook, worksheet, sheetName);
        }

        const wbout = await XLSX.write(newWorkbook);

        const pageSuffix = formatPageRangeForFilename(pagesArray, totalPages);
        const extractedFileName = file.name.replace(/\.(xlsx|xls)$/i, `_sheets_${pageSuffix}.xlsx`);
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const extractedFile = new File([blob], extractedFileName, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        toast.success(`Extracted ${pagesArray.length} sheets`);
        onPagesExtracted(extractedFile, pagesArray, totalPages);
        onClose();
        return;
      }

      const isTextFile = file.type === 'text/plain' ||
                         file.type === 'text/csv' ||
                         file.name.toLowerCase().endsWith('.csv') ||
                         file.name.toLowerCase().endsWith('.txt');

      if (isTextFile && totalPages > 1) {
        const text = await file.text();
        const allLines = text.split('\n');
        const linesPerPage = 45;

        const selectedLines: string[] = [];
        for (const pageNum of pagesArray) {
          const startLine = (pageNum - 1) * linesPerPage;
          const pageLines = allLines.slice(startLine, startLine + linesPerPage);
          selectedLines.push(...pageLines);
        }

        const extractedText = selectedLines.join('\n');
        const pageSuffix = formatPageRangeForFilename(pagesArray, totalPages);
        const fileExt = file.name.match(/\.(txt|csv)$/i)?.[1] || 'txt';
        const extractedFileName = file.name.replace(/\.(txt|csv)$/i, `_pages_${pageSuffix}.${fileExt}`);
        const blob = new Blob([extractedText], { type: file.type });
        const extractedFile = new File([blob], extractedFileName, { type: file.type });

        toast.success(`Extracted ${pagesArray.length} pages`);
        onPagesExtracted(extractedFile, pagesArray, totalPages);
        onClose();
        return;
      }

      if (!isPDF) {
        toast.success('File added');
        onPagesExtracted(file, pagesArray, totalPages);
        onClose();
        return;
      }

      if (!pdfBytes) {
        toast.error('PDF not loaded');
        setProcessing(false);
        return;
      }

      const sourcePdf = await PDFDocument.load(pdfBytes);
      const newPdf = await PDFDocument.create();

      const pageIndices = pagesArray.map(p => p - 1);
      const copiedPages = await newPdf.copyPages(sourcePdf, pageIndices);

      for (const page of copiedPages) {
        newPdf.addPage(page);
      }

      const newPdfBytes = await newPdf.save();

      const pageSuffix = formatPageRangeForFilename(pagesArray, totalPages);
      const extractedFileName = file.name.replace('.pdf', `_pages_${pageSuffix}.pdf`);
      const blob = new Blob([new Uint8Array(newPdfBytes)], { type: 'application/pdf' });
      const extractedFile = new File(
        [blob],
        extractedFileName,
        { type: 'application/pdf' }
      );

      toast.success(`Extracted ${pagesArray.length} pages`);
      onPagesExtracted(extractedFile, pagesArray, totalPages);
      onClose();

    } catch (err: any) {
      console.error('Error extracting pages:', err);
      toast.error('Failed to extract pages: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleSkip = () => {
    onSkip();
    onClose();
  };

  const handleDownloadPages = async () => {
    if (selectedPages.size === 0) {
      toast.error('Please select at least one page to download');
      return;
    }

    setProcessing(true);

    try {
      const pagesArray = Array.from(selectedPages).sort((a, b) => a - b);

      if (isExcel) {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = await XLSX.readAsync(arrayBuffer);

        const newWorkbook = XLSX.utils.book_new();

        for (const pageNum of pagesArray) {
          const sheetIndex = pageNum - 1;
          const worksheet = workbook.sheets[sheetIndex];
          const sheetName = worksheet?.name ?? `Sheet ${pageNum}`;
          XLSX.utils.book_append_sheet(newWorkbook, worksheet, sheetName);
        }

        const wbout = await XLSX.write(newWorkbook);

        const pageSuffix = formatPageRangeForFilename(pagesArray, totalPages);
        const extractedFileName = file.name.replace(/\.(xlsx|xls)$/i, `_sheets_${pageSuffix}.xlsx`);
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // Download the file
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = extractedFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(`Downloaded ${pagesArray.length} sheet${pagesArray.length !== 1 ? 's' : ''}`);
        return;
      }

      if (!isPDF) {
        toast.error('Download is only available for PDF and Excel files');
        return;
      }

      if (!pdfBytes) {
        toast.error('PDF not loaded');
        setProcessing(false);
        return;
      }

      const sourcePdf = await PDFDocument.load(pdfBytes);
      const newPdf = await PDFDocument.create();

      const pageIndices = pagesArray.map(p => p - 1);
      const copiedPages = await newPdf.copyPages(sourcePdf, pageIndices);

      for (const page of copiedPages) {
        newPdf.addPage(page);
      }

      const newPdfBytes = await newPdf.save();

      const pageSuffix = formatPageRangeForFilename(pagesArray, totalPages);
      const extractedFileName = file.name.replace('.pdf', `_pages_${pageSuffix}.pdf`);
      const blob = new Blob([new Uint8Array(newPdfBytes)], { type: 'application/pdf' });

      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = extractedFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${pagesArray.length} page${pagesArray.length !== 1 ? 's' : ''}`);

    } catch (err: any) {
      console.error('Error downloading pages:', err);
      toast.error('Failed to download pages: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              {isPDF ? 'Select Pages to Upload' : 'Confirm File to Upload'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {file.name}
              {isPDF && totalPages > 0 && ` - ${totalPages} pages`}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-gray-600">{isPDF ? 'Loading PDF pages...' : 'Loading file...'}</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={isPDF ? loadPDF : isImage ? loadImage : loadOtherFile}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Controls */}
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Selection buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAll}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      onClick={clearAll}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>

                  {/* Range input */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="e.g., 1-3, 5, 8-10"
                      value={rangeInput}
                      onChange={(e) => setRangeInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && applyRange()}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                    />
                    <button
                      onClick={applyRange}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      Apply
                    </button>
                  </div>

                  {/* Selection count */}
                  <div className="ml-auto text-sm text-gray-600">
                    <span className="font-medium text-blue-600">{selectedPages.size}</span>
                    {' '}of {totalPages} pages selected
                  </div>
                </div>
              </div>

              {/* Thumbnail Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-3">
                  {thumbnails.map((thumbnail) => (
                    <div
                      key={thumbnail.pageNumber}
                      className={`relative cursor-pointer group rounded-lg overflow-hidden border-2 transition-all ${
                        selectedPages.has(thumbnail.pageNumber)
                          ? 'border-blue-500 shadow-md'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => togglePage(thumbnail.pageNumber)}
                    >
                      {/* Thumbnail Image */}
                      <div className="aspect-[3/4] bg-gray-100 relative">
                        {thumbnail.dataUrl ? (
                          <img
                            src={thumbnail.dataUrl}
                            alt={`Page ${thumbnail.pageNumber}`}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                            <FileText className="w-8 h-8" />
                          </div>
                        )}

                        {/* Selection Checkbox */}
                        <div className="absolute top-1 left-1">
                          {selectedPages.has(thumbnail.pageNumber) ? (
                            <CheckSquare className="w-5 h-5 text-blue-600 bg-white rounded" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 bg-white rounded opacity-0 group-hover:opacity-100 transition-opacity" />
                          )}
                        </div>

                        {/* Zoom button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewPage(thumbnail.pageNumber);
                          }}
                          className="absolute top-1 right-1 p-1 bg-white rounded shadow opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <ZoomIn className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>

                      {/* Page Number */}
                      <div className={`text-center py-1 text-xs font-medium ${
                        selectedPages.has(thumbnail.pageNumber)
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {thumbnail.pageNumber}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-center">
            {isPDF ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSkip}
                  disabled={processing || loading}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Upload All Pages
                </button>
                <button
                  onClick={handleDownloadPages}
                  disabled={processing || loading || selectedPages.size === 0}
                  className="px-4 py-2 text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''}
                </button>
              </div>
            ) : (
              <div></div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={processing}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExtractPages}
                disabled={processing || loading || selectedPages.size === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {isPDF ? 'Extracting...' : 'Adding...'}
                  </>
                ) : isPDF ? (
                  <>
                    Upload {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''}
                  </>
                ) : (
                  <>Add File</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewPage && thumbnails.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-60"
          onClick={() => setPreviewPage(null)}
        >
          <div className="relative w-full h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Top Controls Bar */}
            <div className="flex justify-between items-center p-4 bg-black bg-opacity-50">
              <div className="text-white font-medium">
                Page {previewPage} of {totalPages}
              </div>

              {/* Zoom Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setZoomLevel(prev => Math.max(0.25, prev - 0.25))}
                  disabled={zoomLevel <= 0.25}
                  className="p-2 bg-white rounded-lg shadow-lg disabled:opacity-50 hover:bg-gray-100"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-white min-w-[60px] text-center font-medium">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <button
                  onClick={() => setZoomLevel(prev => Math.min(3, prev + 0.25))}
                  disabled={zoomLevel >= 3}
                  className="p-2 bg-white rounded-lg shadow-lg disabled:opacity-50 hover:bg-gray-100"
                  title="Zoom In"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setZoomLevel(0.6)}
                  className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 ml-2"
                  title="Reset Zoom"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              </div>

              <button
                onClick={() => setPreviewPage(null)}
                className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image Container with Scroll */}
            <div className="flex-1 overflow-auto p-4">
              {previewLoading ? (
                <div className="flex items-center justify-center min-h-full">
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-white mx-auto mb-3" />
                    <p className="text-white text-sm">Loading high-resolution preview...</p>
                  </div>
                </div>
              ) : previewImage ? (
                <div className="flex justify-center">
                  <img
                    src={previewImage}
                    alt={`Page ${previewPage}`}
                    style={{
                      width: `${zoomLevel * 100}%`,
                      maxWidth: 'none',
                      transition: 'width 0.2s ease'
                    }}
                    className="rounded-lg shadow-2xl bg-white"
                  />
                </div>
              ) : thumbnails[previewPage - 1]?.dataUrl ? (
                <div className="flex justify-center">
                  <img
                    src={thumbnails[previewPage - 1].dataUrl}
                    alt={`Page ${previewPage}`}
                    style={{
                      width: `${zoomLevel * 100}%`,
                      maxWidth: 'none',
                      transition: 'width 0.2s ease'
                    }}
                    className="rounded-lg shadow-2xl bg-white"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center min-h-full">
                  <div className="flex items-center justify-center w-64 h-80 bg-white rounded-lg">
                    <FileText className="w-16 h-16 text-gray-400" />
                  </div>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="absolute top-1/2 -translate-y-1/2 left-4">
              <button
                onClick={() => setPreviewPage(Math.max(1, previewPage - 1))}
                disabled={previewPage <= 1}
                className="p-3 bg-white rounded-full shadow-lg disabled:opacity-50 hover:bg-gray-100"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 right-4">
              <button
                onClick={() => setPreviewPage(Math.min(totalPages, previewPage + 1))}
                disabled={previewPage >= totalPages}
                className="p-3 bg-white rounded-full shadow-lg disabled:opacity-50 hover:bg-gray-100"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for rendering */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default ClientPDFPageSelector;
